// src/data/editions/firstresponder.js
//
// FIRST RESPONDER edition — police / fire / paramedic / corrections / military
// skin. Police-first in its vocabulary (built for a municipal police
// association) but every label reads correctly for a fire service or
// paramedic operation.
//
// The value chain differs from both the insurer and the operator. A service
// experiences cost as members kept fit for duty; an association experiences
// it as members kept well. Both meet at the same chain:
//   cumulative critical-incident exposure + shift/overtime load
//     -> sick calls & modified duty
//     -> operational stress injury (OSI) leave
//     -> a presumptive psychological-injury claim (provincial workers'
//        compensation for municipal and provincial services; the federal
//        route for the RCMP) + overtime backfill
//     -> long-term leave / medical separation
// So this edition leads with OPERATIONAL AVAILABILITY and treats compensation claims and
// medical separation as the long tail of the same exposure.
//
// All figures are illustrative demo values (summary.source = 'demo' drives
// the console's "Modeled example" badge). Workforce is scaled to a mid-size
// municipal service (sworn + civilian). Palette is navy + gold, illustrative;
// swap in the client's exact hex once supplied.

const firstresponder = {
    id: 'firstresponder',
    label: 'First Responders (Police / Fire / EMS)',

    palette: { deep: '#0B1D3A', blue: '#1F4E8C', yellow: '#C9A227' },

    // Console header titles per tab (EnterpriseConsole.getPageTitle).
    pageTitles: {
        'cc-overview': 'Operational Readiness Overview',
        'cc-triangulation': 'Triangulation: Members, the Service, the Average',
        'cc-data': 'Data Intake: The Service’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Readiness & Strain',
        'cc-predictive': 'Operational Stress & Leave Forecast',
        'cc-roi': 'Program ROI & Operational Availability',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Operational Readiness Command Center'
    },

    // Legend label for the second series on the Overview trend chart.
    trendSignalLabel: 'Sick-leave signal',

    summary: {
        source: 'demo', // drives the console's "Modeled example" badge (T0.3)
        capacityScore: 79,
        capacityTrend: 2,
        workforce: 2100,
        highSignalCohort: 248,
        topPriorityGroup: 'Frontline Patrol',
        topPulseTheme: 'Shift & overtime load',
        participationRate: 58,
        modeledClaimsAvoidance: '$3.4M',
        modeledStdClaims: { low: 34, high: 52, windowDays: 90 },
        modeledLtdTransition: { low: 9, high: 15 },
        leaveProbability: { mid: 21, low: 16, high: 26 },
        // Overview roll-up card copy (2nd + 3rd cards).
        outlookTitle: 'Leave Outlook',
        outlookNoun: 'OSI-related leaves',
        outlookUnit: 'leaves',
        financialNoun: 'backfill & leave-cost avoidance'
    },

    capacityTrend: [
        { date: '1st', capacity: 76, signal: 27 },
        { date: '4th', capacity: 77, signal: 26 },
        { date: '8th', capacity: 75, signal: 29 },
        { date: '12th', capacity: 78, signal: 25 },
        { date: '16th', capacity: 77, signal: 26 },
        { date: '20th', capacity: 79, signal: 23 },
        { date: '24th', capacity: 78, signal: 24 },
        { date: '28th', capacity: 79, signal: 23 },
        { date: 'Today', capacity: 79, signal: 23 }
    ],

    readiness: {
        scoreLabel: 'Operational Readiness Score',
        signalSubtext: 'shift rotation load + overtime hours + incident exposure',
        groups: [
            { name: 'Frontline Patrol', pressure: 84, note: 'Sustained call volume & 12-hour rotations', responses: 142 },
            { name: 'Communications / 911 Dispatch', pressure: 81, note: 'Continuous high-acuity call load', responses: 46 },
            { name: 'Investigations / CID', pressure: 77, note: 'Cumulative exposure & major-case backlog', responses: 68 },
            { name: 'Specialty Units (Tactical, Forensics)', pressure: 74, note: 'Episodic critical-incident exposure', responses: 38 },
            { name: 'Traffic & Court Services', pressure: 69, note: 'Court appearances on rest days', responses: 27 },
            { name: 'Civilian & Support Staff', pressure: 64, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported fatigue trend', value: '+6%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Sick calls & unplanned absence', value: '+8%', tier: 'Lagging', tone: 'amber' },
            { label: 'Overtime backfill hours', value: '+11%', tier: 'Lagging', tone: 'amber' },
            { label: 'Peer-support check-in signal', value: 'Steady', tier: 'Intermediate', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Service-wide', responses: 348 },
            { id: 'patrol', label: 'Frontline Patrol', responses: 142 },
            { id: 'investigations', label: 'Investigations / CID', responses: 68 },
            { id: 'dispatch', label: 'Communications / 911 Dispatch', responses: 46 },
            { id: 'civilian', label: 'Civilian & Support Staff', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Fatigue', sentiment: 'risk', scope: 'patrol',
              text: 'Fourth night in a row and a fatal on the last call. I drove home and do not remember the drive. That is not a wellness issue, that is a road-safety one.' },
            { id: 2, time: '5 hours ago', theme: 'Tools', sentiment: 'positive', scope: 'dispatch',
              text: 'The check-in flagged me before I would have said anything out loud. Got moved off the floor for a set and nobody made it a thing. That is new.' },
            { id: 3, time: 'Yesterday', theme: 'Culture', sentiment: 'positive', scope: 'investigations',
              text: 'The debrief after the major case actually happened this time - same week, peer support in the room. That should be the norm, not the exception.' },
            { id: 4, time: 'Yesterday', theme: 'Workload', sentiment: 'risk', scope: 'patrol',
              text: 'Court on my days off, then straight back onto nights. Overtime is the only way the platoon gets covered and it is eating people.' },
            { id: 5, time: '2 days ago', theme: 'Recognition', sentiment: 'positive', scope: 'patrol',
              text: 'My sergeant noticed I was off and asked - properly, not in passing. Small thing. It mattered.' }
        ],
        sentiment: {
            positiveSummary: 'Strong response to the confidential check-in and to same-week critical-incident debriefs with peer support present.',
            emergingTheme: 'Consecutive night rotations, court appearances on rest days, and overtime backfill are the most frequent sources of fatigue and strain.'
        }
    },

    predictive: {
        title: 'The Operational Stress Funnel',
        intro: 'Cumulative critical-incident exposure combined with sustained shift and overtime load is a leading driver of sick calls, modified duty and operational stress injury (OSI) leaves. In Alberta and Ontario, PTSD in police, fire, paramedic and other first responders is presumptively work-related under provincial compensation law, so those leaves surface as compensation claims and overtime backfill - the operational and financial cost. (Claims for RCMP members run federally.) Long-term leave and medical separation are the long tail of the same exposure. This is directional from leading signals - not a forecast - and needs a 6-12 month window and a comparison baseline before any causal claim.',
        lever: 'Earlier peer-support & clinical routing',
        ownLabel: 'Sick-call & modified-duty rate',
        tiers: [
            { value: '2,100', label: 'Sworn & civilian members', source: 'observed', tone: 'neutral', width: 100 },
            { value: '248', label: 'High-exposure / high-strain cohort', source: 'observed', tone: 'brand', width: 90 },
            { value: '90-130', label: 'Modeled sick calls & modified-duty episodes - qtr', source: 'modeled', tone: 'amber', width: 76 },
            { value: '34-52', label: 'OSI-related leaves -> long-term off duty sick, compensation claims', source: 'modeled', tone: 'amber', width: 60 },
            { value: '9-15', label: 'Long-term leave -> medical separation - tail', source: 'modeled', tone: 'muted', width: 46 }
        ],
        cards: [
            { title: 'Exposure-Absence Association', value: '~4.6x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Units sustaining low readiness and high cumulative exposure show a higher rate of sick calls and modified-duty episodes. In comparable modeled populations the association is roughly 4.6x - directional, and confirmed only against a service baseline.' },
            { title: 'Modeled OSI-Leave Risk', value: '21%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 16-26% band',
              body: 'Modeled probability that sustained strain in a high-exposure unit converts to an OSI-related leave this quarter. The band widens or narrows as service baseline data is added.' },
            { title: 'Proactive Utilization Lift', value: '+34%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'Members engaged with AI Coach recommendations route earlier into peer support, the member assistance program and psychological services - a leading indicator, shown as an association rather than a causal result.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Sick calls and modified-duty episodes are the leading, attributable metrics EAS owns. OSI leaves, compensation claims, long-term leave and medical separation are lagging outcomes that require a longer window and a comparison baseline before any causal claim. Leaves have many causes (physical injury, family, illness); only the operational-stress contribution is modeled.'
        }
    },

    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        // Replacing a trained officer means recruitment, provincial college and
        // field training - close to a year of loaded salary. Turnover is low but
        // each exit is expensive.
        replacementMultiple: 1.0,
        turnoverRate: 6,
        termLabels: {
            lostTime: 'Protected Operational Availability',
            lostTimeBasis: 'Avoided sick-leave & modified-duty days x loaded daily cost x modeled reduction. Daily cost is the conservative floor - overtime backfill and compensation costs run higher and are not booked here.'
        }
    },

    copilot: { exampleScope: 'Frontline Patrol' }
};

export default firstresponder;
